import { ArrowUpRight, BadgeCheck, Wrench } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Reveal } from "@/components/Reveal";
import { SPONSORED_PRODUCTS, AFFILIATE_TOOLS } from "@/config/monetization";
import { fetchMonetization } from "@/lib/api";

export const SponsoredSection = () => {
    const { data: monetization } = useQuery({ queryKey: ["monetization"], queryFn: fetchMonetization });
    const products = monetization?.sponsored_products?.length ? monetization.sponsored_products : SPONSORED_PRODUCTS;
    const tools = monetization?.affiliate_tools?.length ? monetization.affiliate_tools : AFFILIATE_TOOLS;

    return (
    <section id="ferramentas" data-testid="sponsored-section" className="border-t border-white/10 bg-paper">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
                <div>
                    <Reveal>
                        <span className="text-xs uppercase tracking-[0.3em] text-emerald-500 font-medium">Patrocinado</span>
                        <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-3">Ofertas selecionadas</h2>
                        <p className="text-sm text-slate-500 mt-3 max-w-md leading-relaxed">
                            Produtos de parceiros que pagam para estar aqui — e que passaram pelo nosso filtro editorial.
                        </p>
                    </Reveal>
                    <div className="mt-8 flex flex-col gap-4">
                        {products.map((p, i) => (
                            <Reveal key={p.name} delay={i * 0.08}>
                                <a
                                    href={p.url}
                                    target="_blank"
                                    rel="sponsored noopener noreferrer"
                                    data-testid={`sponsored-product-${i}`}
                                    className="group flex items-start gap-4 border border-emerald-500/25 bg-emerald-500/[0.03] hover:border-emerald-500/50 p-5 transition-colors"
                                >
                                    <BadgeCheck size={20} className="text-emerald-500 mt-0.5 shrink-0" />
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-medium text-sm">{p.name}</span>
                                            <span className="text-[9px] uppercase tracking-[0.2em] text-emerald-500 border border-emerald-500/40 px-2 py-0.5">Patrocinado</span>
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{p.description}</p>
                                    </div>
                                    <span className="flex items-center gap-1 text-xs text-emerald-400 whitespace-nowrap mt-1 group-hover:gap-2 transition-all">
                                        {p.cta} <ArrowUpRight size={13} />
                                    </span>
                                </a>
                            </Reveal>
                        ))}
                    </div>
                </div>

                <div>
                    <Reveal>
                        <span className="text-xs uppercase tracking-[0.3em] text-gold font-medium">Recomendados</span>
                        <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-3">Ferramentas que usamos</h2>
                        <p className="text-sm text-slate-500 mt-3 max-w-md leading-relaxed">
                            Recursos que recomendamos de verdade. Alguns links são de afiliado — você não paga nada a mais por isso.
                        </p>
                    </Reveal>
                    <div className="mt-8 flex flex-col">
                        {tools.map((t, i) => (
                            <Reveal key={t.name} delay={i * 0.08}>
                                <a
                                    href={t.url}
                                    target="_blank"
                                    rel="sponsored noopener noreferrer"
                                    data-testid={`affiliate-tool-${i}`}
                                    className="group flex items-center gap-4 py-5 border-b border-white/10 hover:border-gold/40 transition-colors"
                                >
                                    <span className="flex items-center justify-center w-10 h-10 border border-white/10 text-gold shrink-0">
                                        <Wrench size={16} />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-sm group-hover:text-gold transition-colors">{t.name}</span>
                                            <span className="text-[9px] uppercase tracking-[0.2em] text-slate-500 border border-white/10 px-2 py-0.5">{t.tag}</span>
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1 truncate">{t.description}</p>
                                    </div>
                                    <ArrowUpRight size={15} className="text-slate-600 group-hover:text-gold transition-colors shrink-0" />
                                </a>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </section>
    );
};
